/**
 * Nachlaufregel (Nachprovision nach Austritt, 0074) und Sätze je Herkunft als Formular: Einstellungen · Konditionen (Standard
 * der Agentur) und Archiv-Dialog der Stammdaten (Regel für diesen Creator). Rein, ohne Datenbank; Prüfung wie in der Datenbank
 * (validPostTermRule in der Fachlogik).
 */
import { DEAL_ORIGINS, validPostTermRule, type DealOrigin, type OriginRates, type PostTermRule } from "@talentkontor/domain";
import { t } from "../lib/sprache";

export interface StufenZeile {
  /** bis einschließlich Monat nach dem Austritt */
  bis: string;
  satz: string;
}
export interface RegelFormular {
  aktiv: boolean;
  monate: string;
  art: "satz" | "staffel";
  satz: string;
  stufen: StufenZeile[];
  geltung: "concluded" | "initiated";
  laufende: boolean;
  /** Satz laufender Kooperationen nach dem Ende der Regel; leer = 0 (keine Agenturprovision) */
  danach: string;
}

const bpText = (bp: number | undefined) => (bp == null ? "" : String(bp / 100).replace(".", ","));

/** Prozent aus Text („12,5“, „12.5 %“) in Basispunkten; leer = null, ungültig = undefined. */
export function prozentZuBp(v: string): number | null | undefined {
  const t = v.trim().replace(/%$/, "").trim().replace(",", ".");
  if (t === "") return null;
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(t)) return undefined;
  const n = Number(t);
  if (n < 0 || n > 100) return undefined;
  return Math.round(n * 100);
}

export function regelFormular(rule: PostTermRule | null | undefined): RegelFormular {
  if (!rule) return { aktiv: false, monate: "12", art: "satz", satz: "", stufen: [{ bis: "6", satz: "" }, { bis: "12", satz: "" }], geltung: "initiated", laufende: true, danach: "0" };
  return {
    aktiv: true,
    monate: String(rule.months),
    art: rule.steps?.length ? "staffel" : "satz",
    satz: bpText(rule.bp),
    stufen: rule.steps?.length ? rule.steps.map((s) => ({ bis: String(s.upto), satz: bpText(s.bp) })) : [{ bis: String(rule.months), satz: bpText(rule.bp) }],
    geltung: rule.scope ?? "concluded",
    laufende: rule.running ?? true,
    danach: bpText(rule.after_bp ?? 0),
  };
}

/** Formular in die Regel der Datenbank; nicht aktiv = keine Regel. Die letzte Stufe endet mit der Dauer. */
export function regelAusFormular(f: RegelFormular): { rule: PostTermRule | null; fehler: string | null } {
  if (!f.aktiv) return { rule: null, fehler: null };
  const monate = Number(f.monate.trim());
  if (!Number.isInteger(monate) || monate < 1 || monate > 60) return { rule: null, fehler: t("Die Dauer muss zwischen 1 und 60 Monaten liegen.", "The duration must be between 1 and 60 months.") };
  const danach = prozentZuBp(f.danach);
  if (danach === undefined) return { rule: null, fehler: t("Der Satz nach Ablauf muss zwischen 0 und 100 % liegen.", "The rate after expiry must be between 0 and 100 %.") };
  const base = { months: monate, scope: f.geltung, running: f.laufende, ...(danach ? { after_bp: danach } : {}) } as const;
  if (f.art === "satz") {
    const bp = prozentZuBp(f.satz);
    if (bp == null) return { rule: null, fehler: t("Bitte einen Satz zwischen 0 und 100 % angeben.", "Please enter a rate between 0 and 100 %.") };
    return { rule: { ...base, bp }, fehler: null };
  }
  const stufen: { upto: number; bp: number }[] = [];
  let prev = 0;
  for (const [k, z] of f.stufen.entries()) {
    const letzte = k === f.stufen.length - 1;
    const bis = letzte ? monate : Number(z.bis.trim());
    const bp = prozentZuBp(z.satz);
    if (!Number.isInteger(bis) || bis <= prev || bis > monate) return { rule: null, fehler: t(`Stufe ${k + 1}: Die Monate müssen aufsteigen und innerhalb der Dauer liegen.`, `Tier ${k + 1}: the months must ascend and lie within the duration.`) };
    if (bp == null) return { rule: null, fehler: t(`Stufe ${k + 1}: Bitte einen Satz zwischen 0 und 100 % angeben.`, `Tier ${k + 1}: please enter a rate between 0 and 100 %.`) };
    stufen.push({ upto: bis, bp });
    prev = bis;
  }
  if (stufen.length < 1 || stufen.length > 6) return { rule: null, fehler: t("Eine Staffel hat 1 bis 6 Stufen.", "A tier schedule has 1 to 6 tiers.") };
  const rule: PostTermRule = { ...base, steps: stufen };
  return validPostTermRule(rule) ? { rule, fehler: null } : { rule: null, fehler: t("Die Staffel ist unvollständig.", "The tier schedule is incomplete.") };
}

export type HerkunftFormular = Record<DealOrigin, string>;

export function herkunftFormular(rates: OriginRates | null | undefined): HerkunftFormular {
  return { creator: bpText(rates?.creator), agency: bpText(rates?.agency), brand: bpText(rates?.brand) };
}

/** Leere Felder = Standard; kein Eintrag = null (die Datenbank speichert dann nichts). */
export function herkunftAusFormular(f: HerkunftFormular): { rates: OriginRates | null; fehler: string | null } {
  const out: OriginRates = {};
  for (const o of DEAL_ORIGINS) {
    const bp = prozentZuBp(f[o]);
    if (bp === undefined) return { rates: null, fehler: t("Die Sätze je Herkunft müssen zwischen 0 und 100 % liegen.", "The rates per origin must be between 0 and 100 %.") };
    if (bp !== null) out[o] = bp;
  }
  return { rates: Object.keys(out).length ? out : null, fehler: null };
}
